import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  BanknotesIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  BuildingStorefrontIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import {
  BanknotesIcon as BanknotesIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  ClockIcon as ClockIconSolid
} from '@heroicons/react/24/solid';
import { walletsAPI } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

const AdminWallets = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [wallets, setWallets] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [pagination, setPagination] = useState({});

  // Load pending count for tab badge
  const loadPendingCount = async () => {
    try {
      const response = await walletsAPI.getWithdrawalRequests({ status: 'pending' });
      setPendingCount(response.data.data.requests?.length || 0);
    } catch (error) {
      console.log('Failed to load pending count');
    }
  };

  // Load data based on active tab
  useEffect(() => {
    loadData();
    if (activeTab !== 'pending') {
      loadPendingCount(); // تحديث العداد دائماً
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'overview') {
        const walletsResponse = await walletsAPI.getAllWallets({ limit: 50 });
        setWallets(walletsResponse.data.data.wallets || []);
      } else if (activeTab === 'pending') {
        const requestsResponse = await walletsAPI.getWithdrawalRequests({ status: 'pending' });
        const pendingRequests = requestsResponse.data.data.requests || [];
        setWithdrawalRequests(pendingRequests);
        setPendingCount(pendingRequests.length);
        setPagination(requestsResponse.data.data.pagination || {});
      } else if (activeTab === 'history') {
        const [processedResponse, rejectedResponse] = await Promise.all([
          walletsAPI.getWithdrawalRequests({ status: 'processed' }),
          walletsAPI.getWithdrawalRequests({ status: 'rejected' })
        ]);
        const allHistory = [
          ...(processedResponse.data.data.requests || []),
          ...(rejectedResponse.data.data.requests || [])
        ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setWithdrawalRequests(allHistory);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // Handle view request details
  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  // Handle process request
  const handleProcessRequest = (action) => {
    setActionType(action);
    setRejectionReason('');
    setShowDetailsModal(false);
    setShowProcessModal(true);
  };

  // Submit process action
  const handleSubmitProcess = async () => {
    if (!selectedRequest || !actionType) return;

    if (actionType === 'reject' && (!rejectionReason || rejectionReason.trim().length < 10)) {
      toast.error('سبب الرفض مطلوب (10 أحرف على الأقل)');
      return;
    }

    try {
      const data = { action: actionType };
      if (actionType === 'reject') {
        data.rejectionReason = rejectionReason.trim();
      }

      await walletsAPI.processWithdrawalRequest(selectedRequest._id, data);
      
      toast.success(
        actionType === 'approve' 
          ? 'تمت الموافقة على طلب السحب ومعالجته بنجاح' 
          : 'تم رفض طلب السحب'
      );
      
      setShowProcessModal(false);
      setShowDetailsModal(false);
      loadData();
      loadPendingCount(); // تحديث العداد
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل في معالجة الطلب');
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = {
      pending: { color: 'bg-yellow-100 text-yellow-700', text: 'قيد المراجعة', icon: ClockIcon },
      processed: { color: 'bg-green-100 text-green-700', text: 'تم المعالجة', icon: CheckCircleIconSolid },
      rejected: { color: 'bg-red-100 text-red-700', text: 'مرفوض', icon: XCircleIcon }
    };
    
    const statusConfig = config[status] || config.pending;
    const Icon = statusConfig.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
        <Icon className="w-4 h-4 ml-1" />
        {statusConfig.text}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <BanknotesIcon className="h-8 w-8 ml-3 text-blue-600" />
                إدارة المحافظ
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                إدارة محافظ المختبرات ومعالجة طلبات السحب
              </p>
            </div>
            <button
              onClick={loadData}
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <ArrowPathIcon className="h-5 w-5 ml-2" />
              تحديث
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {[
              { id: 'overview', name: 'نظرة عامة', icon: BanknotesIconSolid },
              { id: 'pending', name: 'طلبات السحب المعلقة', icon: ClockIconSolid },
              { id: 'history', name: 'سجل السحوبات', icon: DocumentTextIcon }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5 ml-2" />
                  {tab.name}
                  {tab.id === 'pending' && pendingCount > 0 && (
                    <span className="mr-2 bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs">
                      {pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {wallets.map((wallet) => (
              <motion.div
                key={wallet._id}
                whileHover={{ scale: 1.02, y: -5 }}
                className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
              >
                <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-white truncate">
                        {wallet.lab?.labName}
                      </h3>
                      <p className="text-blue-100 text-sm">محفظة المختبر</p>
                    </div>
                    <div className="p-2 bg-white/20 rounded-lg">
                      <BanknotesIconSolid className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">الرصيد الإجمالي:</span>
                    <span className="font-bold text-gray-900">
                      {wallet.balance?.toLocaleString('ar-SA')} ريال
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">الرصيد المتاح:</span>
                    <span className="font-bold text-emerald-600">
                      {(wallet.balance - (wallet.frozenAmount || 0)).toLocaleString('ar-SA')} ريال
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">الرصيد المجمد:</span>
                    <span className="font-bold text-amber-600">
                      {(wallet.frozenAmount || 0).toLocaleString('ar-SA')} ريال
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Pending Requests Tab */}
        {activeTab === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
          >
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-brand-blueSecondary">
              <div className="flex items-center">
                <ClockIconSolid className="h-6 w-6 text-white mr-3" />
                <h2 className="text-lg font-semibold text-white">
                  طلبات السحب المعلقة ({activeTab === 'pending' ? withdrawalRequests.length : pendingCount})
                </h2>
              </div>
            </div>

            {withdrawalRequests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircleIconSolid className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد طلبات معلقة</h3>
                <p className="text-gray-500">جميع طلبات السحب تمت معالجتها</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {withdrawalRequests.map((request) => (
                  <div key={request._id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{request.requestNumber}</h3>
                            <p className="text-sm text-gray-500">{formatDate(request.createdAt)}</p>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">المختبر:</p>
                            <p className="font-medium text-gray-900">{request.lab?.labName}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">المبلغ:</p>
                            <p className="font-bold text-blue-600">{request.amount.toLocaleString('ar-SA')} ريال</p>
                          </div>
                          <div>
                            <p className="text-gray-600">البنك:</p>
                            <p className="font-medium text-gray-900">{request.bankAccount.bankName}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center mr-4">
                        <button
                          onClick={() => handleViewRequest(request)}
                          className="px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors flex items-center"
                        >
                          <EyeIcon className="h-4 w-4 ml-2" />
                          عرض التفاصيل
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200"
          >
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-brand-blue to-brand-blueSecondary">
              <div className="flex items-center">
                <DocumentTextIcon className="h-6 w-6 text-white mr-3" />
                <h2 className="text-lg font-semibold text-white">سجل السحوبات المعالجة</h2>
              </div>
            </div>

            {withdrawalRequests.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">لا يوجد سجل</h3>
                <p className="text-gray-500">لم يتم معالجة أي طلبات سحب بعد</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {withdrawalRequests.map((request) => (
                  <div key={request._id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{request.requestNumber}</h3>
                            <p className="text-sm text-gray-500">
                              {formatDate(request.createdAt)} - معالج في {formatDate(request.processedAt)}
                            </p>
                          </div>
                          {getStatusBadge(request.status)}
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">المختبر:</p>
                            <p className="font-medium text-gray-900">{request.lab?.labName}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">المبلغ:</p>
                            <p className={`font-bold ${request.status === 'processed' ? 'text-green-600' : 'text-red-600'}`}>
                              {request.amount.toLocaleString('ar-SA')} ريال
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">البنك:</p>
                            <p className="font-medium text-gray-900">{request.bankAccount.bankName}</p>
                          </div>
                          <div>
                            <p className="text-gray-600">معالج بواسطة:</p>
                            <p className="font-medium text-gray-900">{request.processedBy?.name || 'النظام'}</p>
                          </div>
                        </div>

                        {request.rejectionReason && (
                          <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-red-700 font-medium mb-1">سبب الرفض:</p>
                            <p className="text-red-600 text-sm">{request.rejectionReason}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Request Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary px-6 py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">تفاصيل طلب السحب</h3>
                    <p className="text-blue-100 text-sm">{selectedRequest.requestNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-6">
                {/* Request Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">معلومات الطلب</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الطلب:</span>
                        <span className="font-medium">{selectedRequest.requestNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">المختبر:</span>
                        <span className="font-medium">{selectedRequest.lab?.labName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">المبلغ:</span>
                        <span className="font-bold text-blue-600">{selectedRequest.amount.toLocaleString('ar-SA')} ريال</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">تاريخ الطلب:</span>
                        <span className="font-medium">{formatDate(selectedRequest.createdAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">الحالة:</span>
                        {getStatusBadge(selectedRequest.status)}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">بيانات الحساب البنكي</h4>
                    <div className="bg-blue-50 rounded-lg p-4 space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">البنك:</span>
                        <span className="font-medium">{selectedRequest.bankAccount.bankName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">اسم المستفيد:</span>
                        <span className="font-medium">{selectedRequest.bankAccount.accountName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">رقم الآيبان:</span>
                        <span className="font-mono text-sm bg-white px-2 py-1 rounded border">
                          {selectedRequest.bankAccount.iban}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedRequest.notes && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">ملاحظات المختبر</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-gray-700">{selectedRequest.notes}</p>
                    </div>
                  </div>
                )}

                {/* Rejection Reason (if rejected) */}
                {selectedRequest.rejectionReason && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">سبب الرفض</h4>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                      <p className="text-red-700">{selectedRequest.rejectionReason}</p>
                      <p className="text-red-600 text-sm mt-2">
                        تم الرفض بواسطة: {selectedRequest.processedBy?.name || 'النظام'} في {formatDate(selectedRequest.processedAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-between">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إغلاق
                </button>
                
                {selectedRequest.status === 'pending' && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleProcessRequest('reject')}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <HandThumbDownIcon className="h-4 w-4 ml-2" />
                      رفض
                    </button>
                    <button
                      onClick={() => handleProcessRequest('approve')}
                      className="px-4 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors flex items-center"
                    >
                      <HandThumbUpIcon className="h-4 w-4 ml-2" />
                      موافقة وتحويل
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Process Request Modal */}
      <AnimatePresence>
        {showProcessModal && selectedRequest && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowProcessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-brand-blue to-brand-blueSecondary px-6 py-4 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {actionType === 'approve' ? 'الموافقة على طلب السحب' : 'رفض طلب السحب'}
                    </h3>
                    <p className="text-white/80 text-sm">{selectedRequest.requestNumber}</p>
                  </div>
                  <button
                    onClick={() => setShowProcessModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6 space-y-4">
                {/* Quick Summary */}
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <h4 className="font-semibold text-gray-900 mb-2">{selectedRequest.requestNumber}</h4>
                  <p className="text-2xl font-bold text-blue-600">{selectedRequest.amount.toLocaleString('ar-SA')} ريال</p>
                  <p className="text-sm text-gray-600 mt-1">إلى {selectedRequest.bankAccount.bankName}</p>
                </div>

                {actionType === 'approve' ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <CheckCircleIcon className="h-5 w-5 text-blue-600 ml-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-blue-800 font-medium">تأكيد الموافقة</p>
                        <p className="text-blue-700 text-sm mt-1">
                          سيتم خصم المبلغ من المحفظة فوراً وإنشاء معاملة السحب
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-gray-600 ml-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-gray-800 font-medium">رفض الطلب</p>
                          <p className="text-gray-700 text-sm mt-1">
                            يرجى كتابة سبب واضح للرفض ليتم إرساله للمختبر
                          </p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        سبب الرفض <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="4"
                        placeholder="مثال: البيانات البنكية غير مكتملة أو غير صحيحة..."
                        maxLength="500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {rejectionReason.length}/500 حرف (الحد الأدنى 10 أحرف)
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Modal Footer */}
              <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
                <button
                  onClick={() => setShowProcessModal(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleSubmitProcess}
                  className="px-6 py-2 bg-gradient-to-r from-brand-blue to-brand-blueSecondary text-white rounded-lg hover:opacity-95 transition-colors flex items-center"
                >
                  {actionType === 'approve' ? (
                    <>
                      <HandThumbUpIcon className="h-4 w-4 ml-2" />
                      موافقة وتحويل
                    </>
                  ) : (
                    <>
                      <HandThumbDownIcon className="h-4 w-4 ml-2" />
                      رفض الطلب
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminWallets;